import EditIcon from '@mui/icons-material/Edit';
import {
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { settingsRoutes } from '../../../../routes';
import { useAiPrompts } from '../../../../sic-lib/api/hooks/useAiPrompts';
import { AiPromptFeatures } from '../../../../sic-lib/types/Ai';
import AxiosErrorAlert from '../../../Shared/Alerts/AxiosErrorAlert';

const featureOptions = Object.keys(AiPromptFeatures)
    .filter(key => isNaN(Number(key)))
    .filter(key => key !== 'SmartReply'); // Remove SmartReply from filter options - created using different form

export default function AiPromptManagement() {
    const history = useHistory();
    const { data: prompts, isLoading, isError, error } = useAiPrompts();
    const [featureFilter, setFeatureFilter] = useState<AiPromptFeatures | undefined>(undefined);

    const filteredPrompts = useMemo(() => {
        if (featureFilter === undefined) {
            return prompts?.filter(prompt => prompt.feature !== AiPromptFeatures.SmartReply); // Return all prompts if no filter is selected
        }
        return prompts
            ?.filter(prompt => prompt.feature === featureFilter)
            ?.filter(prompt => prompt.feature !== AiPromptFeatures.SmartReply);
    }, [prompts, featureFilter]);

    const handleNavigateToEditNew = (promptId?: number) => {
        history.push(settingsRoutes.setCreateEditPrompt(promptId));
    };

    const handleFilterByFeature = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        // Convert string back to enum value or undefined for "All"
        const feature =
            value === 'undefined'
                ? undefined
                : AiPromptFeatures[value as keyof typeof AiPromptFeatures];
        setFeatureFilter(feature);
    };

    return (
        <>
            <Paper>
                <Grid container justifyContent="space-between" alignItems="center" p={2}>
                    <Grid item>
                        <Typography variant="h5">Ai Prompts</Typography>
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <FormControl sx={{ minWidth: 150 }} size="small">
                                    <InputLabel id="prompt-filter-by-feature-label">
                                        Filter by Feature
                                    </InputLabel>
                                    <Select
                                        labelId="prompt-filter-by-feature-label"
                                        id="prompt-filter-by-feature"
                                        value={
                                            featureFilter !== undefined
                                                ? AiPromptFeatures[featureFilter]
                                                : 'undefined'
                                        }
                                        label="Filter by Feature"
                                        onChange={handleFilterByFeature}
                                    >
                                        <MenuItem value="undefined">All</MenuItem>
                                        {featureOptions.map(key => {
                                            return (
                                                <MenuItem key={key} value={key}>
                                                    {key}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <Button
                                    onClick={() => handleNavigateToEditNew(undefined)}
                                    variant="outlined"
                                >
                                    New Prompt
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Divider />
                {isLoading && (
                    <Grid display="flex" justifyContent="center" m={2}>
                        <CircularProgress />
                    </Grid>
                )}
                <TableContainer>
                    <Table>
                        <TableBody>
                            {!isLoading &&
                                filteredPrompts &&
                                filteredPrompts.length > 0 &&
                                filteredPrompts.map(prompt => (
                                    <TableRow
                                        key={prompt.promptId}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>
                                            <Typography variant="body1">{prompt.title}</Typography>
                                            <Typography variant="caption">
                                                {prompt.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Grid
                                                container
                                                spacing={1}
                                                alignItems="center"
                                                justifyContent="flex-end"
                                            >
                                                <Grid item>
                                                    <Chip
                                                        label={AiPromptFeatures[prompt.feature]}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Chip
                                                        label={
                                                            prompt.clientId
                                                                ? `Client: ${prompt.clientId}`
                                                                : 'Global'
                                                        }
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </Grid>
                                                {prompt.userName && (
                                                    <Grid item>
                                                        <Chip
                                                            label={prompt.userName}
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Grid>
                                                )}
                                                <Grid item>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="edit"
                                                        onClick={() =>
                                                            handleNavigateToEditNew(prompt.promptId)
                                                        }
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {isError && <AxiosErrorAlert error={error} />}
            </Paper>
        </>
    );
}
